import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  is_read?: boolean;
  createdAt: string;
  created_at?: string;
  priority?: string;
  data?: any;
  metadata?: any;
}

interface Filter {
  id: string;
  name: string;
  count: number;
}

const Notifications: React.FC = () => {
  const colors = getThemeColors('dark');

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [invitationsCount, setInvitationsCount] = useState(0);

  // Mock notifications data
  const mockNotifications: Notification[] = [
    {
      id: '1',
      title: 'Chama Invitation',
      message: 'You have been invited to join "Savings Group" chama. Monthly contribution: KES 5,000',
      type: 'chama_invitation',
      isRead: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
      data: {
        chamaId: 'chama-1',
        inviterName: 'John Doe',
        chamaName: 'Savings Group',
        contributionAmount: 5000,
        contributionFrequency: 'monthly'
      }
    },
    {
      id: '2',
      title: 'Loan Application Approved',
      message: 'Your loan application for KES 50,000 has been approved. Funds will be disbursed within 24 hours.',
      type: 'loan_approved',
      isRead: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
      data: { chamaId: 'chama-1', loanId: 'loan-1' }
    },
    {
      id: '3',
      title: 'Meeting Scheduled',
      message: 'Monthly chama meeting scheduled for tomorrow at 2:00 PM. Please confirm your attendance.',
      type: 'meeting_scheduled',
      isRead: true,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(), // 4 hours ago
      data: { chamaId: 'chama-1', meetingId: 'meeting-1' }
    },
    {
      id: '4',
      title: 'Contribution Recorded',
      message: 'Your monthly contribution of KES 5,000 has been successfully recorded.',
      type: 'contribution_recorded',
      isRead: true,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
      data: { chamaId: 'chama-1', transactionId: 'txn-1' }
    },
    {
      id: '5',
      title: 'New Member Joined',
      message: 'Jane Smith has joined your chama group.',
      type: 'member_joined',
      isRead: true,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
      data: { chamaId: 'chama-1' }
    },
    {
      id: '6',
      title: 'Guarantor Request',
      message: 'You have been requested to guarantee a loan application for KES 25,000.',
      type: 'guarantor_request',
      isRead: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(), // 6 hours ago
      data: {
        loan_id: 'loan-2',
        guarantor_id: 'guarantor-1',
        amount: 25000,
        purpose: 'Business expansion'
      }
    },
    {
      id: '7',
      title: 'Support Request Update',
      message: 'Your support request #1234 has been updated. Our team has reviewed your issue.',
      type: 'support_update',
      isRead: false,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(), // 8 hours ago
      data: { supportRequestId: 'req-1234' }
    },
    {
      id: '8',
      title: 'System Maintenance',
      message: 'Scheduled maintenance will occur tonight from 2:00 AM to 4:00 AM. Service may be temporarily unavailable.',
      type: 'system',
      isRead: true,
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(), // 3 days ago
      priority: 'high'
    }
  ];

  useEffect(() => {
    loadNotifications();
    loadInvitationsCount();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setNotifications(mockNotifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvitationsCount = async () => {
    try {
      // Mock invitations count
      setInvitationsCount(2);
    } catch (error) {
      console.error('Failed to load invitations count:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadNotifications();
      await loadInvitationsCount();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          isRead: true,
          is_read: true
        }))
      );
      alert('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      alert('Failed to mark all notifications as read');
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, isRead: true, is_read: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      alert('Notification deleted successfully');
    } catch (error) {
      console.error('Failed to delete notification:', error);
      alert('Failed to delete notification');
    }
  };

  const handleNotificationPress = async (notification: Notification) => {
    if (!notification.isRead && !notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'chama_invitation':
        showInvitationDialog(notification);
        break;
      case 'guarantor_request':
        showGuarantorDialog(notification);
        break;
      case 'chama':
      case 'member_joined':
        if (notification.data?.chamaId) {
          alert(`Navigate to Chama Details: ${notification.data.chamaId}`);
        }
        break;
      case 'meeting_scheduled':
      case 'meeting_started':
      case 'meeting_created':
        if (notification.data?.chamaId) {
          alert(`Navigate to Chama Meetings: ${notification.data.chamaId}`);
        }
        break;
      case 'loan_application_submitted':
      case 'loan_approved':
      case 'loan_disbursed':
      case 'loan_rejected':
        if (notification.data?.chamaId) {
          alert(`Navigate to Chama Loans: ${notification.data.chamaId}`);
        }
        break;
      case 'contribution_recorded':
        if (notification.data?.chamaId) {
          alert(`Navigate to Chama Contributions: ${notification.data.chamaId}`);
        }
        break;
      case 'support_update':
      case 'new_support_request':
        alert('Navigate to Support/Contact page');
        break;
      default:
        break;
    }
  };

  const showInvitationDialog = (notification: Notification) => {
    const data = notification.data;
    const accept = window.confirm(
      `Chama Invitation\n\n${data.inviterName} invited you to join ${data.chamaName}\n\nContribution: KES ${data.contributionAmount} ${data.contributionFrequency}\n\nDo you want to accept this invitation?`
    );

    if (accept) {
      respondToInvitation(notification.id, 'accept');
    } else {
      respondToInvitation(notification.id, 'reject');
    }
  };

  const respondToInvitation = async (invitationId: string, action: 'accept' | 'reject') => {
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 500));

      if (action === 'accept') {
        alert('You have successfully joined the chama!');
      } else {
        alert('Invitation rejected successfully');
      }

      // Remove the invitation notification
      setNotifications(prev => prev.filter(n => n.id !== invitationId));
    } catch (error) {
      alert('Failed to respond to invitation');
    }
  };

  const showGuarantorDialog = (notification: Notification) => {
    const data = notification.data;
    const accept = window.confirm(
      `Guarantor Request\n\nYou have been requested to guarantee a loan of KES ${data.amount}\n\nPurpose: ${data.purpose || 'Not specified'}\n\nDo you accept to be a guarantor for this loan?`
    );

    if (accept) {
      respondToGuarantorRequest(notification.id, 'accept');
    } else {
      respondToGuarantorRequest(notification.id, 'decline');
    }
  };

  const respondToGuarantorRequest = async (guarantorId: string, action: 'accept' | 'decline') => {
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 500));

      if (action === 'accept') {
        alert('You have accepted to be a guarantor for this loan!');
      } else {
        alert('You have declined the guarantor request');
      }

      // Mark as read
      await markAsRead(guarantorId);
    } catch (error) {
      alert('Failed to respond to guarantor request');
    }
  };

  const getFilteredNotifications = () => {
    let filtered = notifications;

    switch (selectedFilter) {
      case 'unread':
        filtered = notifications.filter(n => !(n.isRead || n.is_read));
        break;
      case 'chama':
        filtered = notifications.filter(n =>
          n.type === 'chama' ||
          n.type === 'chama_invitation' ||
          n.type === 'member_joined'
        );
        break;
      case 'financial':
        filtered = notifications.filter(n =>
          n.type === 'financial' ||
          n.type.includes('contribution') ||
          n.type.includes('loan') ||
          n.type === 'guarantor_request'
        );
        break;
      case 'system':
        filtered = notifications.filter(n => n.type === 'system');
        break;
      case 'support':
        filtered = notifications.filter(n =>
          n.type === 'support_update' ||
          n.type === 'new_support_request'
        );
        break;
      default:
        break;
    }

    return filtered.sort((a, b) =>
      new Date(b.createdAt || b.created_at || 0).getTime() -
      new Date(a.createdAt || a.created_at || 0).getTime()
    );
  };

  const getNotificationIcon = (type: string, priority?: string) => {
    switch (type) {
      case 'chama_invitation':
        return '📧';
      case 'guarantor_request':
        return '🛡️';
      case 'chama':
      case 'member_joined':
        return '👥';
      case 'meeting_scheduled':
      case 'meeting_started':
      case 'meeting_created':
        return '📅';
      case 'loan_application_submitted':
      case 'loan_approved':
      case 'loan_disbursed':
      case 'loan_rejected':
        return '💰';
      case 'contribution_recorded':
        return '💳';
      case 'financial':
        return '💵';
      case 'support_update':
      case 'new_support_request':
        return '❓';
      case 'system':
        return priority === 'high' ? '⚠️' : 'ℹ️';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type: string, priority?: string) => {
    switch (type) {
      case 'chama_invitation':
        return colors.primary;
      case 'guarantor_request':
        return colors.warning;
      case 'chama':
      case 'member_joined':
        return colors.primary;
      case 'meeting_scheduled':
      case 'meeting_started':
      case 'meeting_created':
        return colors.info;
      case 'loan_application_submitted':
      case 'loan_approved':
      case 'loan_disbursed':
      case 'loan_rejected':
        return colors.warning;
      case 'contribution_recorded':
      case 'financial':
        return colors.success;
      case 'support_update':
      case 'new_support_request':
        return colors.info;
      case 'system':
        return priority === 'high' ? colors.error : colors.secondary;
      default:
        return colors.textSecondary;
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return time.toLocaleDateString();
  };

  const filterCounts = useMemo(() => {
    const all = notifications.length;
    const unread = notifications.filter(n => !(n.isRead || n.is_read)).length;
    const chama = notifications.filter(n =>
      n.type === 'chama' ||
      n.type === 'chama_invitation' ||
      n.type === 'member_joined'
    ).length;
    const financial = notifications.filter(n =>
      n.type === 'financial' ||
      n.type.includes('contribution') ||
      n.type.includes('loan') ||
      n.type === 'guarantor_request'
    ).length;
    const support = notifications.filter(n =>
      n.type === 'support_update' ||
      n.type === 'new_support_request'
    ).length;
    const system = notifications.filter(n => n.type === 'system').length;

    return { all, unread, chama, financial, support, system };
  }, [notifications]);

  const filters: Filter[] = [
    { id: 'all', name: 'All', count: filterCounts.all },
    { id: 'unread', name: 'Unread', count: filterCounts.unread },
    { id: 'chama', name: 'Chama', count: filterCounts.chama },
    { id: 'financial', name: 'Financial', count: filterCounts.financial },
    { id: 'support', name: 'Support', count: filterCounts.support },
    { id: 'system', name: 'System', count: filterCounts.system },
  ];

  const filteredNotifications = getFilteredNotifications();

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
    },
    menuButton: {
      position: 'relative',
      padding: spacing.xs,
      borderRadius: borderRadius.md,
      cursor: 'pointer',
      color: colors.text,
    },
    invitationIndicator: {
      position: 'absolute',
      top: -2,
      right: -2,
      minWidth: 16,
      height: 16,
      borderRadius: 8,
      backgroundColor: colors.error,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: 4,
      paddingRight: 4,
    },
    invitationCount: {
      fontSize: 10,
      fontWeight: typography.fontWeight.bold,
      color: colors.white,
      lineHeight: '12px',
    },
    markAllButton: {
      paddingLeft: spacing.xs,
      paddingRight: spacing.xs,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
    },
    markAllText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
      color: colors.primary,
    },
    filtersContainer: {
      display: 'flex',
      gap: spacing.xs,
      overflowX: 'auto',
      paddingBottom: spacing.xs,
    },
    filterChip: {
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
      borderRadius: borderRadius.lg,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.backgroundSecondary,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      minHeight: 32,
      display: 'flex',
      alignItems: 'center',
    },
    filterChipSelected: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    filterText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
      color: colors.textSecondary,
    },
    filterTextSelected: {
      color: colors.white,
    },
    notificationsList: {
      paddingBottom: spacing.xl,
    },
    notificationCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      border: '1px solid',
      borderColor: colors.border,
      cursor: 'pointer',
      ...getShadowStyle('sm'),
    },
    notificationCardUnread: {
      borderColor: colors.primary,
      borderWidth: 2,
      backgroundColor: 'rgba(33, 150, 243, 0.08)',
    },
    notificationHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: spacing.md,
    },
    notificationIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: 'rgba(33, 150, 243, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.lg,
      flexShrink: 0,
    },
    notificationInfo: {
      flex: 1,
    },
    titleRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: spacing.xs,
    },
    notificationTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      flex: 1,
      marginRight: spacing.md,
    },
    unreadDot: {
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: colors.primary,
      flexShrink: 0,
    },
    notificationTime: {
      fontSize: typography.fontSize.sm,
      color: colors.textTertiary,
      marginTop: spacing.xs,
    },
    notificationMessage: {
      fontSize: typography.fontSize.base,
      color: colors.text,
      lineHeight: 1.5,
      marginBottom: spacing.md,
    },
    actionButtonsRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: spacing.sm,
      paddingTop: spacing.sm,
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
    },
    guarantorActions: {
      display: 'flex',
      flex: 1,
      justifyContent: 'space-between',
      gap: spacing.sm,
    },
    guarantorStatus: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      padding: spacing.sm,
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      borderRadius: borderRadius.md,
      border: '1px solid #4CAF50',
      gap: spacing.xs,
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      borderRadius: borderRadius.md,
      gap: spacing.xs,
      cursor: 'pointer',
    },
    acceptButton: {
      backgroundColor: 'rgba(76, 175, 80, 0.15)',
    },
    rejectButton: {
      backgroundColor: 'rgba(244, 67, 54, 0.15)',
    },
    markReadButton: {
      backgroundColor: 'rgba(33, 150, 243, 0.15)',
    },
    deleteButton: {
      backgroundColor: 'rgba(244, 67, 54, 0.15)',
      border: '1px solid #f44336',
    },
    actionButtonText: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
    },
    actionButtonTextAccept: {
      color: '#4CAF50',
    },
    actionButtonTextReject: {
      color: '#f44336',
    },
    actionButtonTextMarkRead: {
      color: '#2196F3',
    },
    actionButtonTextDelete: {
      color: '#f44336',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: spacing.xxxl,
      paddingBottom: spacing.xxxl,
      minHeight: 400,
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    emptyTitle: {
      fontSize: 28,
      fontWeight: '700',
      color: colors.text,
      marginBottom: spacing.lg,
      textAlign: 'center',
    },
    emptySubtitle: {
      fontSize: 18,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 1.5,
      maxWidth: 300,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading notifications...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Notifications"
        subtitle="Stay updated with your chama activities"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerTop}>
              <div style={styles.headerActions}>
                <button
                  style={styles.menuButton}
                  onClick={() => window.location.href = '/dashboard/settings'}
                  title="Settings"
                >
                  ⚙️
                </button>

                <button
                  style={styles.menuButton}
                  onClick={() => window.location.href = '/dashboard/reminders'}
                  title="Reminders"
                >
                  ⏰
                </button>

                <button
                  style={styles.menuButton}
                  onClick={() => window.location.href = '/dashboard/settings/notification-tone'}
                  title="Notification Tones"
                >
                  🎵
                </button>

                <button
                  style={styles.menuButton}
                  onClick={() => window.location.href = '/dashboard/invitations'}
                  title="Invitations"
                >
                  ✉️
                  {invitationsCount > 0 && (
                    <div style={styles.invitationIndicator}>
                      <span style={styles.invitationCount}>
                        {invitationsCount > 9 ? '9+' : invitationsCount}
                      </span>
                    </div>
                  )}
                </button>

                {filterCounts.unread > 0 && (
                  <button
                    style={styles.markAllButton}
                    onClick={markAllAsRead}
                  >
                    <span style={styles.markAllText}>Mark all read</span>
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div style={styles.filtersContainer}>
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  style={{
                    ...styles.filterChip,
                    ...(selectedFilter === filter.id ? styles.filterChipSelected : {}),
                  }}
                  onClick={() => setSelectedFilter(filter.id)}
                >
                  <span style={{
                    ...styles.filterText,
                    ...(selectedFilter === filter.id ? styles.filterTextSelected : {}),
                  }}>
                    {filter.name} ({filter.count})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Notifications List */}
          <div style={styles.notificationsList}>
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notification) => {
            const isRead = notification.isRead || notification.is_read;
            return (
              <div
                key={notification.id}
                style={{
                  ...styles.notificationCard,
                  ...(!isRead ? styles.notificationCardUnread : {}),
                }}
                onClick={() => handleNotificationPress(notification)}
              >
                <div style={styles.notificationHeader}>
                  <div style={styles.notificationIcon}>
                    <span style={{ fontSize: 24 }}>
                      {getNotificationIcon(notification.type, notification.priority)}
                    </span>
                  </div>

                  <div style={styles.notificationInfo}>
                    <div style={styles.titleRow}>
                      <h3 style={styles.notificationTitle}>
                        {notification.title}
                      </h3>
                      {!isRead && <div style={styles.unreadDot} />}
                    </div>
                    <div style={styles.notificationTime}>
                      {getTimeAgo(notification.createdAt || notification.created_at || '')}
                    </div>
                  </div>
                </div>

                <p style={styles.notificationMessage}>
                  {notification.message}
                </p>

                {/* Action Buttons */}
                <div style={styles.actionButtonsRow}>
                  {notification.type === 'guarantor_request' ? (
                    <div style={styles.guarantorActions}>
                      {isRead ? (
                        <div style={styles.guarantorStatus}>
                          <span>✅</span>
                          <span style={styles.actionButtonText}>Already Responded</span>
                        </div>
                      ) : (
                        <>
                          <button
                            style={{ ...styles.actionButton, ...styles.acceptButton }}
                            onClick={(e) => {
                              e.stopPropagation();
                              respondToGuarantorRequest(notification.id, 'accept');
                            }}
                          >
                            <span>✅</span>
                            <span style={{ ...styles.actionButtonText, ...styles.actionButtonTextAccept }}>
                              Accept
                            </span>
                          </button>

                          <button
                            style={{ ...styles.actionButton, ...styles.rejectButton }}
                            onClick={(e) => {
                              e.stopPropagation();
                              respondToGuarantorRequest(notification.id, 'decline');
                            }}
                          >
                            <span>❌</span>
                            <span style={{ ...styles.actionButtonText, ...styles.actionButtonTextReject }}>
                              Decline
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                  ) : !isRead ? (
                    <button
                      style={{ ...styles.actionButton, ...styles.markReadButton }}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                    >
                      <span>✓</span>
                      <span style={{ ...styles.actionButtonText, ...styles.actionButtonTextMarkRead }}>
                        Mark Read
                      </span>
                    </button>
                  ) : null}

                  <button
                    style={{ ...styles.actionButton, ...styles.deleteButton }}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                  >
                    <span>🗑️</span>
                    <span style={{ ...styles.actionButtonText, ...styles.actionButtonTextDelete }}>
                      Delete
                    </span>
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              {selectedFilter === 'unread' ? '✅' : '🔔'}
            </div>
            <h2 style={styles.emptyTitle}>
              {selectedFilter === 'unread' ? 'All caught up!' : 'No notifications'}
            </h2>
            <p style={styles.emptySubtitle}>
              {selectedFilter === 'unread'
                ? 'You have no unread notifications'
                : 'You\'ll see notifications here when they arrive'
              }
            </p>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Refresh indicator */}
      {refreshing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: spacing.md,
          backgroundColor: colors.primary,
          color: colors.white,
          textAlign: 'center',
          zIndex: 1000,
        }}>
          Refreshing...
        </div>
      )}
    </div>
  );
};

export default Notifications;
